import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Container,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    Box,
    Link
} from '@mui/material';
import {
    School as SchoolIcon,
    Article as ArticleIcon,
    Assessment as AssessmentIcon,
    Group as GroupIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const Home = () => {
    const { isAuthenticated } = useAuth();
    
    const features = [
        {
            icon: <SchoolIcon sx={{ fontSize: 40 }} />,
            title: 'Comprehensive Learning',
            description: 'Access a wide range of medical topics and subjects through our structured courses and materials.'
        },
        {
            icon: <ArticleIcon sx={{ fontSize: 40 }} />,
            title: 'Medical Articles',
            description: 'Stay updated with the latest medical research and clinical practices through our curated articles.'
        },
        {
            icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
            title: 'Practice Exams',
            description: 'Test your knowledge with our comprehensive practice exams and track your progress.'
        },
        {
            icon: <GroupIcon sx={{ fontSize: 40 }} />,
            title: 'Community Learning',
            description: 'Join a community of medical professionals and students to share knowledge and experiences.'
        }
    ];

    return (
        <Box>
            {/* Hero Section */}
            <Box
                sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    py: 8,
                    mb: 6
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Typography variant="h2" component="h1" gutterBottom>
                                Medical Learning Platform
                            </Typography>
                            <Typography variant="h5" sx={{ mb: 4 }}>
                                Your comprehensive platform for medical education and professional development
                            </Typography>
                            {!isAuthenticated && (
                                <Box>
                                    <Button
                                        component={RouterLink}
                                        to="/register"
                                        variant="contained"
                                        color="secondary"
                                        size="large"
                                        sx={{ mr: 2 }}
                                    >
                                        Get Started
                                    </Button>
                                    <Button
                                        component={RouterLink}
                                        to="/login"
                                        variant="outlined"
                                        color="inherit"
                                        size="large"
                                    >
                                        Sign In
                                    </Button>
                                </Box>
                            )}
                            {isAuthenticated && (
                                <Button
                                    component={RouterLink}
                                    to="/dashboard"
                                    variant="contained"
                                    color="secondary"
                                    size="large"
                                >
                                    Go to Dashboard
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Features Section */}
            <Container maxWidth="lg" sx={{ mb: 8 }}>
                <Typography variant="h3" component="h2" align="center" gutterBottom>
                    Why Choose Our Platform?
                </Typography>
                <Grid 
                    container 
                    spacing={4} 
                    sx={{ 
                        mt: 4,
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            md: 'repeat(4, 1fr)'
                        },
                        gap: 4,
                        justifyContent: 'center'
                    }}
                >
                    {features.map((feature, index) => (
                        <Grid 
                            item 
                            key={index}
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                width: '100%'
                            }}
                        >
                            <Card 
                                sx={{ 
                                    width: '100%',
                                    maxWidth: 300,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    textAlign: 'center'
                                }}
                            >
                                <CardContent 
                                    sx={{ 
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        width: '100%'
                                    }}
                                >
                                    <Box 
                                        sx={{ 
                                            color: 'primary.main', 
                                            mb: 2,
                                            display: 'flex',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        {feature.icon}
                                    </Box>
                                    <Typography 
                                        variant="h6" 
                                        component="h3" 
                                        gutterBottom
                                        sx={{ textAlign: 'center' }}
                                    >
                                        {feature.title}
                                    </Typography>
                                    <Typography 
                                        color="text.secondary"
                                        sx={{ textAlign: 'center' }}
                                    >
                                        {feature.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* CTA Section */}
            <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
                <Container maxWidth="md">
                    <Typography variant="h4" component="h2" align="center" gutterBottom>
                        Ready to Start Your Medical Learning Journey?
                    </Typography>
                    <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 4 }}>
                        Join thousands of medical professionals and students who are already learning with us.
                    </Typography>
                    {!isAuthenticated && (
                        <Box sx={{ textAlign: 'center' }}>
                            <Button
                                component={RouterLink}
                                to="/register"
                                variant="contained"
                                color="primary"
                                size="large"
                            >
                                Create Your Account
                            </Button>
                        </Box>
                    )}
                    {isAuthenticated && (
                        <Box sx={{ textAlign: 'center' }}>
                            <Button
                                component={RouterLink}
                                to="/exams"
                                variant="contained"
                                color="primary"
                                size="large"
                            >
                                Browse Available Exams
                            </Button>
                        </Box>
                    )}
                </Container>
            </Box>
        </Box>
    );
};

export default Home; 